import { getCookie } from "cookies-next";
import { axiosInstance } from "../../rest/axios";
import {
  CustomTemplate,
  DbCustomTemplateMedia,
  TemplateRequest,
} from "./models";
import {
  Firestore,
  collection,
  doc,
  setDoc,
  Timestamp,
  query,
  getDocs,
  orderBy,
  where,
  getDoc,
} from "firebase/firestore/lite";
import { firebaseDb } from "../../firebase/client-app";
import { whatsAppCustomHeaderService } from "../custom-header/service";

interface TemplateMediaResponse {
  handleId: string;
}

export class WhatsAppCustomTemplateService {
  private templateMediaRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/whatsapp_template_media",
    );
  }

  async getCustomMessageTemplates(appId: string): Promise<CustomTemplate[]> {
    try {
      const templates = await axiosInstance.get(
        `/whatsapp/message/custom-templates?app_id=${appId}`,
        {
          headers: { Authorization: getCookie("Authorization")?.toString() },
        },
      );
      return templates.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteCustomMessageTemplate(
    elementName: string,
    templateId: string,
    appId: string,
  ): Promise<any> {
    try {
      await axiosInstance.delete(
        `/whatsapp/message/custom-template/${templateId}`,
        {
          headers: { Authorization: getCookie("Authorization")?.toString() },
          data: {
            element_name: elementName,
            app_id: appId,
          },
        },
      );

      return {
        success: true,
      };
    } catch (error: any) {
      throw error;
    }
  }

  async createCustomTemplate(templateDetails: CustomTemplate): Promise<void> {
    try {
      const requestData: TemplateRequest = {
        appId: templateDetails.appId,
        elementName: templateDetails.elementName,
        languageCode: templateDetails.languageCode,
        category: templateDetails.category,
        vertical: templateDetails.vertical,
        templateType: templateDetails.templateType,
        content: templateDetails.content,
        header: templateDetails.header,
        exampleHeader: templateDetails.exampleHeader,
        footer: templateDetails.footer,
        example: templateDetails.example,
        exampleMedia: templateDetails.exampleMedia,
        enableSample: templateDetails.enableSample || false,
        buttons: templateDetails.buttons,
      };

      const response = await axiosInstance.post(
        "/whatsapp/message/custom-template",
        requestData,
        {
          headers: { Authorization: getCookie("Authorization")?.toString() },
        },
      );

      return response.data.success;
    } catch (error: any) {
      throw error;
    }
  }

  async editCustomTemplate(templateDetails: CustomTemplate): Promise<void> {
    try {
      const requestData: TemplateRequest = {
        appId: templateDetails.appId,
        templateId: templateDetails.id,
        elementName: templateDetails.elementName,
        languageCode: templateDetails.languageCode,
        category: templateDetails.category,
        vertical: templateDetails.vertical,
        templateType: templateDetails.templateType,
        content: templateDetails.content,
        header: templateDetails.header,
        exampleHeader: templateDetails.exampleHeader,
        footer: templateDetails.footer,
        example: templateDetails.example,
        exampleMedia: templateDetails.exampleMedia,
        enableSample: templateDetails.enableSample || false,
        buttons: templateDetails.buttons,
        status: templateDetails.status,
      };

      const response = await axiosInstance.put(
        "/whatsapp/message/custom-template",
        requestData,
        {
          headers: { Authorization: getCookie("Authorization")?.toString() },
        },
      );

      return response.data.success;
    } catch (error: any) {
      throw error;
    }
  }

  async getTemplateMediaByHandleId(
    businessId: string,
    handleId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbCustomTemplateMedia | null> {
    try {
      const mediaRef = this.templateMediaRef(firestore, businessId);
      const querySnapshot = await getDocs(
        query(mediaRef, where("handle_id", "==", handleId)),
      );

      if (querySnapshot.empty) {
        return null;
      }
      const docSnap = querySnapshot.docs[0];
      return docSnap.data() as DbCustomTemplateMedia;
    } catch (error: any) {
      throw error;
    }
  }

  async uploadTemplateMedia(
    file: string,
    fileType: string,
    businessId: string,
  ): Promise<TemplateMediaResponse> {
    try {
      const customHeader =
        await whatsAppCustomHeaderService.getWhatsAppCustomHeader(businessId);
      if (!customHeader?.app_id) {
        throw new Error("App ID not found");
      }

      const res = await axiosInstance.post(
        "/whatsapp/message/custom-template/media",
        {
          file,
          file_type: fileType,
          app_id: customHeader.app_id,
        },
        {
          headers: { Authorization: getCookie("Authorization")?.toString() },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error("Template media upload failed:", error);
      throw error;
    }
  }

  async addTemplateMedia(
    businessId: string,
    url: string,
    handleId: string,
    fileType: string,
    blurHash?: string,
    firestore: Firestore = firebaseDb,
  ) {
    try {
      const media: DbCustomTemplateMedia = {
        url,
        handle_id: handleId,
        blur_hash: blurHash || "",
        file_type: fileType,
        created_at: Timestamp.now(),
      };

      const docRef = doc(this.templateMediaRef(firestore, businessId));
      await setDoc(docRef, media);

      return media;
    } catch (error: any) {
      console.error("Failed to store template media:", error);
      throw error;
    }
  }

  async getCustomTemplateMedia(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbCustomTemplateMedia[]> {
    const q = query(
      this.templateMediaRef(firestore, businessId),
      orderBy("created_at", "desc"),
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => doc.data() as DbCustomTemplateMedia);
  }

  async getCanbookHeaderWhatsappTemplates(
    firestore: Firestore = firebaseDb,
  ): Promise<string[]> {
    const docRef = doc(
      firestore,
      "app_config",
      "canbook_header_whatsapp_templates",
    );
    const snapshot = await getDoc(docRef);

    if (snapshot.exists()) {
      return snapshot.data().templates || [];
    }

    return [];
  }
}

export const whatsAppCustomTemplateService =
  new WhatsAppCustomTemplateService();
